import React, { useRef, useState } from 'react';
import { MAX_UPLOAD_FILES_CHIP } from '../../../utils/uploadNames';
import { Box, Chip, Paper, Tooltip, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import CallSplitIcon from '@mui/icons-material/CallSplit';
import TableChartIcon from '@mui/icons-material/TableChart';

// One destination on the workspace board: a standing container for everything routed to
// a table, and a drop target that sends files straight there without triage. Purely
// presentational — it owns no Firestore access and no approval logic.
//
// A bucket that cannot carry the current attribute selection refuses the drop rather
// than accepting it and silently discarding the attributes: the backend
// (allocate_workspace_file.run_relay) applies the same rule server-side. Refused drops
// are still forwarded so the parent's guard can say why. The blocked look (dim +
// tooltip) applies only while the bucket is empty — one holding cards is live work in
// flight, and its cards stay fully interactive whatever the current selection is.
function TableBucket({
  title,
  count = 0,
  isMulti = false,
  isCarrier = true,
  blockedReason,
  isDropTarget = true,
  onFilesDropped,
  // The last drop on this bucket held more files than one upload may take.
  overCap = false,
  children,
}) {
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  // Depth counter, not a boolean: dragleave fires whenever the pointer crosses into a
  // child element, so a boolean flickers the highlight off while still inside the bucket.
  const dragDepth = useRef(0);
  const inputRef = useRef(null);

  const droppable = isDropTarget && isCarrier;
  const blockedLook = !isCarrier && count === 0;

  const resetDrag = () => {
    dragDepth.current = 0;
    setIsDraggingOver(false);
  };

  const handleDragEnter = (e) => {
    if (!isDropTarget) return;
    e.preventDefault();
    e.stopPropagation();
    dragDepth.current += 1;
    if (droppable) setIsDraggingOver(true);
  };

  const handleDragOver = (e) => {
    if (!isDropTarget) return;
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = droppable ? 'copy' : 'none';
  };

  const handleDragLeave = (e) => {
    if (!isDropTarget) return;
    e.preventDefault();
    e.stopPropagation();
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) setIsDraggingOver(false);
  };

  const handleDrop = (e) => {
    if (!isDropTarget) return;
    e.preventDefault();
    e.stopPropagation();
    resetDrag();
    // Non-carrier drops are forwarded too: the parent's carrier guard rejects them
    // with the table name and reason, instead of this bucket refusing silently.
    onFilesDropped(e.dataTransfer.files);
  };

  const handleBrowse = () => {
    if (!droppable) return;
    inputRef.current?.click();
  };

  const bucket = (
    <Paper
      variant="outlined"
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        // Standing height so an empty destination still reads as a real container and
        // gives the drop a generous target; buckets with cards grow past it.
        minHeight: 420,
        borderRadius: 1,
        overflow: 'hidden',
        transition: 'border-color 0.2s, background-color 0.2s, opacity 0.2s',
        ...(blockedLook ? { opacity: 0.45, cursor: 'not-allowed' } : {}),
        ...(overCap ? { borderColor: 'error.main' } : {}),
        ...(isDraggingOver
          ? {
            borderColor: 'primary.main',
            backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.08),
          }
          : {}),
      }}
    >
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: 2,
        py: 1.25,
        borderBottom: '1px solid',
        borderColor: 'divider',
        backgroundColor: (theme) => alpha(theme.palette.common.white, 0.03),
      }}>
        {isMulti
          ? <CallSplitIcon fontSize="small" sx={{ color: 'text.secondary' }} />
          : <TableChartIcon fontSize="small" sx={{ color: 'text.secondary' }} />}
        {/* The destination is the new information on this screen, so it reads at full
            opacity while the file names inside the cards stay secondary. */}
        <Typography variant="subtitle1" sx={{ fontWeight: 600, minWidth: 0, flexGrow: 1 }} noWrap>
          {title}
        </Typography>
        {count > 0 && <Chip label={count} size="small" />}
        {overCap && <Chip label={MAX_UPLOAD_FILES_CHIP} color="error" size="small" />}
      </Box>

      <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2, flexGrow: 1,
        maxHeight: 720, overflowY: 'auto', '& > *': { flexShrink: 0 } }}>
        {children}
        {count === 0 && isDropTarget && (
          <Box
            onClick={handleBrowse}
            sx={{
              // Fills the empty bucket rather than sitting as a strip at the top, so the
              // whole card reads as the drop target it already is.
              flexGrow: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '2px dashed',
              borderColor: isDraggingOver ? 'primary.main' : 'divider',
              borderRadius: 1,
              p: 2,
              textAlign: 'center',
              cursor: droppable ? 'pointer' : 'not-allowed',
              '&:hover': droppable ? { borderColor: 'primary.main' } : {},
            }}
          >
            <Typography variant="body2" color="text.secondary">
              {isCarrier
                ? 'Drop files here to send them straight to this table'
                : 'Cannot receive this drop'}
            </Typography>
          </Box>
        )}
      </Box>

      {isDropTarget && (
        <input
          ref={inputRef}
          type="file"
          multiple
          style={{ display: 'none' }}
          onChange={(e) => {
            onFilesDropped(e.target.files);
            e.target.value = null;
          }}
        />
      )}
    </Paper>
  );

  if (!blockedLook || !blockedReason) return bucket;
  return (
    <Tooltip title={blockedReason}>
      <Box sx={{ height: '100%' }}>{bucket}</Box>
    </Tooltip>
  );
}

export default TableBucket;
